import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Bell, Search, X, Filter } from 'lucide-react';
import { teas } from '../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Define filter categories with their matching keywords
const FILTER_CATEGORIES = [
  { id: 'all', label: 'All Products', keywords: [] },
  { id: 'blends', label: 'Specialty Teas', keywords: [] },
  { id: 'herbs', label: 'Single Herbs', keywords: [] },
  { id: 'energy', label: 'Energy', keywords: ['Energy', 'Vitality', 'Energizing', 'Contains Caffeine'] },
  { id: 'sleep', label: 'Sleep', keywords: ['Sleep', 'Calming', 'Deep Sleep'] },
  { id: 'focus', label: 'Focus', keywords: ['Focus', 'Mental Clarity', 'Clarity', 'Brain Health', 'Mental Energy'] },
  { id: 'detox', label: 'Detox', keywords: ['Detox', 'Detox Support', 'Cleansing', 'Purifying', 'Chelation', 'Internal Health'] },
  { id: 'immune', label: 'Immune', keywords: ['Immune Support', 'Immunity', 'Vitamin C', 'Antioxidants', 'Antioxidant Rich'] },
  { id: 'digestive', label: 'Digestive', keywords: ['Digestive Aid', 'Soothing', 'Comfort', 'Gut', 'Anti-Bloating', 'Anti-Nausea'] },
  { id: 'stress', label: 'Stress Relief', keywords: ['Stress Relief', 'Adaptogenic', 'Mood Support', 'Balance', 'Relaxation'] },
  { id: 'pain', label: 'Pain Relief', keywords: ['Pain Relief', 'Joint Support', 'Muscle Relief', 'Anti-inflammatory'] },
];

export const Products = ({ onInquire }) => {
  const [stockStatus, setStockStatus] = useState({});
  const [prices, setPrices] = useState({});
  const [notifyEmail, setNotifyEmail] = useState({});
  const [notifyStatus, setNotifyStatus] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [labelVisibility, setLabelVisibility] = useState({});
  const [customProducts, setCustomProducts] = useState([]);
  const [promoBanner, setPromoBanner] = useState({ text: 'Buy 4 Bags, Get FREE Shipping!', enabled: true });

  useEffect(() => {
    // Fetch stock status, prices, label visibility, custom products, and promo banner from database
    const fetchData = async () => {
      try {
        const [stockRes, pricesRes, labelsRes, customRes, promoRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/stock`),
          fetch(`${API_URL}/api/admin/prices`),
          fetch(`${API_URL}/api/admin/labels`),
          fetch(`${API_URL}/api/products/custom`),
          fetch(`${API_URL}/api/promo-banner`)
        ]);
        if (stockRes.ok) {
          const stockData = await stockRes.json();
          setStockStatus(stockData);
        }
        if (pricesRes.ok) {
          const pricesData = await pricesRes.json();
          setPrices(pricesData);
        }
        if (labelsRes.ok) {
          const labelsData = await labelsRes.json();
          setLabelVisibility(labelsData);
        }
        if (customRes.ok) {
          const customData = await customRes.json();
          setCustomProducts(customData);
        }
        if (promoRes.ok) {
          const promoData = await promoRes.json();
          setPromoBanner(promoData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  // Combine default and custom products
  const allTeas = useMemo(() => [...teas, ...customProducts], [customProducts]);

  // Filter teas based on search query and active filter
  const filteredTeas = useMemo(() => {
    return allTeas.filter(tea => {
      // Hide out_of_stock teas completely from public view
      const status = stockStatus.hasOwnProperty(tea.id) ? stockStatus[tea.id] : (tea.inStock === false ? 'out_of_stock' : 'in_stock');
      if (status === 'out_of_stock') return false;

      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = searchQuery === '' || 
        tea.name.toLowerCase().includes(searchLower) ||
        tea.description.toLowerCase().includes(searchLower) ||
        (tea.purpose && tea.purpose.toLowerCase().includes(searchLower)) ||
        (tea.benefits && tea.benefits.some(b => b.toLowerCase().includes(searchLower)));

      const filterCategory = FILTER_CATEGORIES.find(f => f.id === activeFilter);
      
      if (activeFilter === 'blends') {
        return matchesSearch && tea.category !== 'single-herb';
      }
      if (activeFilter === 'herbs') {
        return matchesSearch && tea.category === 'single-herb';
      }
      
      const matchesFilter = activeFilter === 'all' || 
        (tea.benefits && tea.benefits.some(benefit => 
          filterCategory.keywords.some(keyword => 
            benefit.toLowerCase().includes(keyword.toLowerCase())
          )
        ));

      return matchesSearch && matchesFilter;
    });
  }, [searchQuery, activeFilter, allTeas, stockStatus]);

  // Split into herbs first, then specialty teas
  const singleHerbs = useMemo(() => filteredTeas.filter(t => t.category === 'single-herb'), [filteredTeas]);
  const specialtyTeas = useMemo(() => filteredTeas.filter(t => t.category !== 'single-herb'), [filteredTeas]);

  const clearFilters = () => {
    setSearchQuery('');
    setActiveFilter('all');
  };

  const getStockStatus = (tea) => {
    // Check database override first, then fall back to default
    if (stockStatus.hasOwnProperty(tea.id)) {
      const status = stockStatus[tea.id];
      // Handle backwards compatibility - if it's a boolean, convert it
      if (typeof status === 'boolean') {
        return status ? 'in_stock' : 'out_of_stock';
      }
      return status;
    }
    // Default to in stock
    if (tea.inStock === false) return 'out_of_stock';
    return 'in_stock';
  };

  // Get status display info
  const getStatusInfo = (status) => {
    return {
      'in_stock': { label: '✓ In Stock', bgColor: '#3a692b', canBuy: true },
      'out_of_stock': { label: '✗ Out of Stock', bgColor: '#8b5c2f', canBuy: false },
      'coming_soon': { label: '⏳ Coming Soon', bgColor: '#f59e0b', canBuy: false }
    }[status] || { label: '✓ In Stock', bgColor: '#3a692b', canBuy: true };
  };

  const getPrice = (tea) => {
    // Check database override first, then fall back to default
    if (prices.hasOwnProperty(tea.id)) {
      return prices[tea.id];
    }
    return tea.price;
  };

  const getLabelVisibility = (tea) => {
    // Check database override first, then fall back to default (show labels)
    if (labelVisibility.hasOwnProperty(tea.id)) {
      return labelVisibility[tea.id];
    }
    return true;
  };

  const handleInquiry = (tea) => {
    if (onInquire) {
      onInquire(tea.name);
    }
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNotifySubmit = async (tea) => {
    const email = notifyEmail[tea.id];
    if (!email) return;

    try {
      const response = await fetch(`${API_URL}/api/notify-stock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email,
          product_id: tea.id,
          product_name: tea.name
        })
      });
      const data = await response.json();
      setNotifyStatus(prev => ({ ...prev, [tea.id]: data.message }));
      setNotifyEmail(prev => ({ ...prev, [tea.id]: '' }));
      
      // Clear message after 5 seconds
      setTimeout(() => {
        setNotifyStatus(prev => ({ ...prev, [tea.id]: null }));
      }, 5000);
    } catch (error) {
      setNotifyStatus(prev => ({ ...prev, [tea.id]: 'Something went wrong. Please try again.' }));
    }
  };

  return (
    <section id="products" className="py-20" style={{ backgroundColor: 'white' }}>
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <h2
            className="text-4xl md:text-5xl font-bold mb-4"
            style={{ 
              color: '#5e3a1c',
              fontFamily: 'Georgia, serif'
            }}
          >
            Our Herbal Brew Collection
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: '#8b5c2f' }}>
            Artisanal blends crafted with nature's finest ingredients
          </p>
          
          {/* Free Shipping Promo - Dynamic */}
          {promoBanner.enabled && (
            <div 
              className="mt-6 inline-block px-6 py-3 rounded-full font-bold"
              style={{ backgroundColor: '#3a692b', color: '#f5e9d0' }}
            >
              {promoBanner.text}
            </div>
          )}
        </div>

        {/* Search and Filter Section */}
        <div className="mb-10 max-w-4xl mx-auto">
          {/* Search Box */}
          <div className="relative mb-6">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: '#8b5c2f' }} />
            <Input
              type="text"
              placeholder="Search teas by name, benefit, or purpose..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-10 py-6 text-lg rounded-full"
              style={{ 
                borderColor: '#c2a357', 
                borderWidth: '2px',
                backgroundColor: '#faf6ee'
              }}
              data-testid="product-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 p-1 rounded-full hover:bg-gray-200"
                data-testid="clear-search-btn"
              >
                <X className="w-5 h-5" style={{ color: '#8b5c2f' }} />
              </button>
            )}
          </div>

          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-2 mb-4">
            <Filter className="w-5 h-5 self-center mr-2" style={{ color: '#8b5c2f' }} />
            {FILTER_CATEGORIES.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  activeFilter === filter.id 
                    ? 'shadow-md transform scale-105' 
                    : 'hover:shadow-sm'
                }`}
                style={{ 
                  backgroundColor: activeFilter === filter.id ? '#8b5c2f' : '#f5e9d0',
                  color: activeFilter === filter.id ? '#f5e9d0' : '#5e3a1c',
                  border: '1px solid #c2a357'
                }}
                data-testid={`filter-btn-${filter.id}`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {/* Results Count & Clear */}
          <div className="flex justify-between items-center text-sm" style={{ color: '#8b5c2f' }}>
            <span data-testid="results-count">
              Showing {filteredTeas.length} of {allTeas.length} teas
            </span>
            {(searchQuery || activeFilter !== 'all') && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-1 rounded-full hover:bg-gray-100"
                style={{ color: '#8b5c2f' }}
                data-testid="clear-filters-btn"
              >
                <X className="w-4 h-4" />
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* No Results Message */}
        {filteredTeas.length === 0 && (
          <div className="text-center py-12" data-testid="no-results-message">
            <p className="text-xl mb-4" style={{ color: '#5e3a1c' }}>
              No teas found matching your search
            </p>
            <p className="mb-4" style={{ color: '#8b5c2f' }}>
              Try adjusting your search or filter criteria
            </p>
            <Button
              onClick={clearFilters}
              style={{ backgroundColor: '#8b5c2f', color: '#f5e9d0' }}
            >
              View All Teas
            </Button>
          </div>
        )}

        {/* Render a tea card */}
        {(() => {
          const renderTeaCard = (tea) => {
            const status = getStockStatus(tea);
            const statusInfo = getStatusInfo(status);
            const showLabels = getLabelVisibility(tea);
            return (
            <Card
              key={tea.id}
              className="group hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 hover:-translate-y-1 product-card-glow"
              style={{ borderColor: '#c2a357' }}
            >
              {showLabels && (tea.imageFront || tea.imageBack) ? (
                <div className="relative h-96 overflow-hidden bg-white">
                  <div className="flex gap-2 p-2 h-full">
                    {tea.imageFront && (
                      <div className="flex-1 flex items-center justify-center">
                        <img
                          src={tea.imageFront}
                          alt={`${tea.name} - Front`}
                          className="h-full w-auto object-contain group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    )}
                    {tea.imageBack && (
                      <div className="flex-1 flex items-center justify-center">
                        <img
                          src={tea.imageBack}
                          alt={`${tea.name} - Back`}
                          className="h-full w-auto object-contain group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    )}
                  </div>
                  
                  {tea.badge && (
                    <div
                      className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide badge-shimmer"
                      style={{ 
                        backgroundColor: tea.badge === 'bestseller' ? '#c2a357' : tea.badge === 'popular' ? '#8b5c2f' : '#3a692b',
                        color: '#ffffff'
                      }}
                    >
                      {tea.badge === 'bestseller' ? '⭐ Bestseller' : tea.badge === 'popular' ? '🔥 Popular' : '✨ New'}
                    </div>
                  )}
                  
                  <div
                    className="absolute top-4 left-4 px-3 py-1 rounded-full text-sm font-bold"
                    style={{ 
                      backgroundColor: statusInfo.bgColor,
                      color: '#f5e9d0'
                    }}
                  >
                    {statusInfo.label}
                  </div>
                </div>
              ) : (
                <div className="relative h-48 overflow-hidden flex items-center justify-center" style={{ backgroundColor: '#f5e9d0' }}>
                  <div className="text-center">
                    <div className="text-6xl mb-2">🍵</div>
                    <p className="text-sm" style={{ color: '#8b5c2f' }}>{tea.purpose || 'Herbal Tea'}</p>
                  </div>
                  <div
                    className="absolute top-4 left-4 px-3 py-1 rounded-full text-sm font-bold"
                    style={{ 
                      backgroundColor: statusInfo.bgColor,
                      color: '#f5e9d0'
                    }}
                  >
                    {statusInfo.label}
                  </div>
                </div>
              )}
              
              <CardHeader>
                <CardTitle style={{ color: '#5e3a1c' }}>{tea.name}</CardTitle>
                <CardDescription style={{ color: '#8b5c2f' }}>
                  {tea.description}
                </CardDescription>
              </CardHeader>
              
              <CardContent>
                {tea.benefits && tea.benefits.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {tea.benefits.map((benefit, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        style={{ backgroundColor: '#3a692b', color: '#f5e9d0' }}
                      >
                        {benefit}
                      </Badge>
                    ))}
                  </div>
                )}
                
                {tea.warning && (
                  <div 
                    className="mb-3 p-2 rounded text-xs font-medium"
                    style={{ backgroundColor: '#fff3cd', color: '#856404', border: '1px solid #ffc107' }}
                  >
                    {tea.warning}
                  </div>
                )}
                
                <p className="text-2xl font-bold" style={{ color: '#c2a357' }}>
                  {getPrice(tea)} <span className="text-sm font-normal">+ shipping</span>
                </p>
                {tea.weight && (
                  <p className="text-sm mt-1" style={{ color: '#8b5c2f' }}>
                    {tea.weight}
                  </p>
                )}
              </CardContent>
              
              <CardFooter className="flex flex-col gap-2">
                {statusInfo.canBuy ? (
                  <Button
                    onClick={() => handleInquiry(tea)}
                    className="w-full"
                    style={{ backgroundColor: '#8b5c2f', color: '#f5e9d0' }}
                  >
                    Inquire Now
                  </Button>
                ) : status === 'coming_soon' ? (
                  <div className="w-full">
                    <Button
                      disabled
                      className="w-full"
                      style={{ backgroundColor: '#f59e0b', color: '#ffffff', opacity: 0.8 }}
                    >
                      Coming Soon
                    </Button>
                    <p className="text-xs text-center mt-2" style={{ color: '#8b5c2f' }}>
                      This tea is being crafted and will be available soon!
                    </p>
                  </div>
                ) : (
                  <div className="w-full">
                    {notifyStatus[tea.id] ? (
                      <p className="text-sm text-center py-2" style={{ color: '#3a692b' }}>
                        {notifyStatus[tea.id]}
                      </p>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          type="email"
                          placeholder="Your email"
                          value={notifyEmail[tea.id] || ''}
                          onChange={(e) => setNotifyEmail(prev => ({ ...prev, [tea.id]: e.target.value }))}
                          className="flex-1 text-sm"
                          style={{ borderColor: '#c2a357' }}
                        />
                        <Button
                          onClick={() => handleNotifySubmit(tea)}
                          size="sm"
                          style={{ backgroundColor: '#3a692b', color: '#f5e9d0' }}
                          title="Notify me when back in stock"
                        >
                          <Bell className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    <p className="text-xs text-center mt-2" style={{ color: '#8b5c2f' }}>
                      Get notified when back in stock
                    </p>
                  </div>
                )}
              </CardFooter>
            </Card>
          )};

          return (
            <>
              {/* Single Herbs Section */}
              {singleHerbs.length > 0 && (
                <>
                  <div className="text-center mb-8" data-testid="single-herbs-section">
                    <h3 className="text-2xl md:text-3xl font-bold" style={{ color: '#5e3a1c', fontFamily: 'Georgia, serif' }}>
                      Single Herb Series
                    </h3>
                    <p className="text-sm mt-1" style={{ color: '#8b5c2f' }}>Pure, single-ingredient loose leaf teas</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                    {singleHerbs.map(renderTeaCard)}
                  </div>
                </>
              )}

              {/* Divider Bar - Specialty Teas */}
              {specialtyTeas.length > 0 && singleHerbs.length > 0 && (
                <div className="relative my-12" data-testid="specialty-divider">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full" style={{ borderTop: '2px solid #c2a357' }}></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span 
                      className="px-8 py-3 text-xl md:text-2xl font-bold tracking-wide"
                      style={{ 
                        backgroundColor: '#5e3a1c', 
                        color: '#c2a357',
                        fontFamily: 'Georgia, serif',
                        borderRadius: '999px',
                        border: '2px solid #c2a357'
                      }}
                    >
                      Specialty Tea Blends
                    </span>
                  </div>
                </div>
              )}

              {/* Specialty Teas Section */}
              {specialtyTeas.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {specialtyTeas.map(renderTeaCard)}
                </div>
              )}
            </>
          );
        })()}
      </div>
    </section>
  );
};
